
import React, { useState, useEffect } from 'react';
import { X, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isEEAUser, setIsEEAUser] = useState(false);

  useEffect(() => {
    // Check if user has already made a consent choice
    const consentChoice = localStorage.getItem('google-analytics-consent');
    console.log('Consent choice from localStorage:', consentChoice);
    
    // Simple EEA detection (you may want to use a more sophisticated method)
    const checkEEALocation = async () => {
      try {
        // This is a simplified check - in production you might want to use a more reliable geolocation service
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        console.log('User timezone:', timezone);
        
        const eeaTimezones = [
          'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Rome',
          'Europe/Madrid', 'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Vienna',
          'Europe/Stockholm', 'Europe/Copenhagen', 'Europe/Helsinki', 'Europe/Oslo',
          'Europe/Warsaw', 'Europe/Prague', 'Europe/Budapest', 'Europe/Bucharest',
          'Europe/Sofia', 'Europe/Zagreb', 'Europe/Ljubljana', 'Europe/Bratislava',
          'Europe/Tallinn', 'Europe/Riga', 'Europe/Vilnius', 'Europe/Dublin',
          'Europe/Athens', 'Europe/Lisbon', 'Europe/Luxembourg', 'Europe/Malta',
          'Europe/Nicosia'
        ];
        
        const isEEA = eeaTimezones.some(tz => timezone.includes(tz.split('/')[1]));
        console.log('Is EEA user:', isEEA);
        setIsEEAUser(isEEA);
        
        // For testing purposes, show banner if no consent choice has been made
        // Remove the EEA check temporarily to make it visible for testing
        if (!consentChoice) {
          console.log('Showing consent banner');
          setShowBanner(true);
        } else {
          console.log('Not showing banner - consent already given:', consentChoice);
        }
      } catch (error) {
        console.log('Could not determine location, showing banner as precaution');
        // Show banner as precaution if we can't determine location
        if (!consentChoice) {
          setShowBanner(true);
        }
      }
    };

    checkEEALocation();
  }, []);

  const handleAcceptAll = () => {
    console.log('User accepted all cookies');
    localStorage.setItem('google-analytics-consent', 'accepted');
    setShowBanner(false);
    
    // Send consent signals to Google Analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
  };

  const handleRejectAll = () => {
    console.log('User rejected all advertising cookies');
    localStorage.setItem('google-analytics-consent', 'rejected');
    setShowBanner(false);
    
    // Send consent signals to Google Analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      });
    }
  };

  const handleAnalyticsOnly = () => {
    console.log('User accepted analytics only');
    localStorage.setItem('google-analytics-consent', 'analytics-only');
    setShowBanner(false);
    
    // Send consent signals to Google Analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      });
    }
  };

  console.log('ConsentBanner render - showBanner:', showBanner);

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1">
            <Info className="h-5 w-5 text-primary" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-2">
              Your Privacy Choices
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              We use Google Analytics to improve our website and services. We need your consent to share your personal data with Google for advertising purposes. 
              You can choose to accept all cookies, reject advertising cookies, or allow only essential analytics.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleAcceptAll}
                className="bg-primary hover:bg-primary/90 text-white"
                size="sm"
              >
                Accept All
              </Button>
              <Button
                onClick={handleAnalyticsOnly}
                variant="outline"
                size="sm"
              >
                Analytics Only
              </Button>
              <Button
                onClick={handleRejectAll}
                variant="outline"
                size="sm"
              >
                Reject All
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              You can change your preferences at any time. 
              <a href="/privacy-policy" className="text-primary hover:underline ml-1">
                Learn more about our privacy practices
              </a>
            </p>
          </div>
          
          <button
            onClick={handleRejectAll}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            aria-label="Close banner"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
